import { LoadingOutlined } from '@ant-design/icons';
import { CloseIcon } from '@assets/icons/CloseIcon';
import { SubscriptionsIcon } from '@assets/icons/SubscriptionsIcon';
import {
  BarChartIcon,
  CalendarDateRangeIcon,
  ContrastIcon,
  SatelliteIcon,
  TimeSeriesIcon,
} from '@assets/icons/minor';
import { SupportedEntitlements } from '@core/models/entitlements';
import { Reports } from '@core/models/reports/reports.models';
import { usePrevious } from '@hooks/UI/usePrevious';
import { useBulkXlsxReports } from '@hooks/queries/bulkXlsx/useBulkXlsxReports';
import { useRetryBulkXlsxReport } from '@hooks/queries/bulkXlsx/useRetryBulkXlsxReport';
import { useOrganization } from '@hooks/queries/useOrganization';
import { useReports } from '@hooks/queries/useReports';
import { useStoredReports } from '@hooks/queries/useStoredReports';
import { useEntitlements } from '@hooks/useEntitlements';
import { useImageryFlags } from '@hooks/useImageryFlags';
import { Drawer, Spin, message } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Dictionary } from 'redux-ngrx-entity';
import { Amplitude } from 'src/amplitude';
import ExportedReports from './ExportedReports/ExportedReports';
import { storeReportOnIndexedDB } from './ReportsDrawer.utils';

interface ReportsDrawerProps {
  visible: boolean;
  onClose: () => void;
  setNDVIReportDrawerVisible?: (visible: boolean) => void;
  setReportNotifications: (n: number) => void;
  onOpenBulkXlsxReport?: (type: 'timeseries' | 'histogram') => void;
}

const ReportsDrawer: React.FC<ReportsDrawerProps> = ({
  visible,
  onClose,
  setNDVIReportDrawerVisible,
  setReportNotifications,
  onOpenBulkXlsxReport,
}) => {
  const entitlements = useEntitlements();
  const { t } = useTranslation();
  const { organization } = useOrganization();
  const navigate = useNavigate();
  const { FlagShowNdviReportDrawer } = useImageryFlags(organization);
  const { queries, triggerReportGeneration } = useReports();
  const { mutate: retryBulkXlsx } = useRetryBulkXlsxReport();
  const hasAccessToWeeklyFieldInsightsReport =
    FlagShowNdviReportDrawer ||
    entitlements.has(SupportedEntitlements.WEEKLY_FIELD_INSIGHTS_REPORT);
  const { data: storedReports = [] } = useStoredReports(organization?.workspace_id, visible);
  const { reports: bulkXlsxReports } = useBulkXlsxReports({
    workspaceId: organization?.workspace_id,
    enabled: visible,
  });

  const BULK_XLSX_TYPES = useMemo(
    () =>
      new Set<Reports.ReportTypes>([
        Reports.ReportTypes.TIME_SERIES_XLSX,
        Reports.ReportTypes.HISTOGRAM_XLSX,
      ]),
    []
  );

  const validQueries = useMemo(() => {
    const nonBulkStoredReports = storedReports.filter((r) => !BULK_XLSX_TYPES.has(r.type));

    const currentQueries = queries.reduce<Array<Reports.ReportQueryData>>((acc, curQuery) => {
      if (
        curQuery.data &&
        !nonBulkStoredReports.some(
          (r) => r.type === curQuery.data?.type && r.generated_at === curQuery.data?.generated_at
        )
      ) {
        acc.push(curQuery.data);
      }
      return acc;
    }, []);

    // currentQueries carries raw UseQueryResult entries from useReports()'s useQueries() call;
    // spreading them here into the combined report list is pre-existing and unrelated to this change.
    // eslint-disable-next-line @tanstack/query/no-rest-destructuring
    return [...currentQueries, ...nonBulkStoredReports, ...bulkXlsxReports];
  }, [queries, storedReports, bulkXlsxReports, BULK_XLSX_TYPES]);

  const loadingReports = useMemo(() => {
    const loading: Dictionary<boolean> = {};
    queries.forEach((query, index) => {
      const type = Object.values(Reports.ReportTypes)[index];
      loading[type] = query.isFetching && query.data?.status !== Reports.ReportStatus.AVAILABLE;
    });
    return loading;
  }, [queries]);

  const previousReportQueries = usePrevious(validQueries);

  useEffect(() => {
    validQueries.forEach((query) => {
      if (!query.is_new || !query?.type || !organization) return;
      const previousQuery = previousReportQueries?.find((q) =>
        query.report_id ? q?.report_id === query.report_id : q?.type === query.type
      );
      if (
        (!previousQuery && query.status === Reports.ReportStatus.GENERATING) ||
        query.status !== previousQuery?.status
      ) {
        const messageKey = `header.reports.messages.${query.status.toLowerCase()}`;
        switch (query.status) {
          case Reports.ReportStatus.ERROR:
            message.error(t(messageKey), 2.5);
            break;
          case Reports.ReportStatus.AVAILABLE:
            message.success(t(messageKey), 2.5);
            storeReportOnIndexedDB({ ...query, is_new: false }, organization?.workspace_id);
            setReportNotifications(1);
            break;
          default:
            message.info(t(messageKey), 2.5);
            break;
        }
      }
    });
  }, [validQueries, previousReportQueries, t, setReportNotifications, organization]);
  const handleOpenSubscriptions = () => {
    onClose();
    const url = `/app/organizations/${organization?.id}/subscriptions`;
    navigate(url);
  };

  const onClickGenerateReport = (type: Reports.ReportTypes) => {
    const reportAlreadyExportedToday = validQueries.some(
      (report) => report.type === type && dayjs(report.generated_at).isSame(dayjs(), 'day')
    );
    if (reportAlreadyExportedToday) {
      message.success(t('header.reports.messages.report_already_generated_today'), 2.5);
      return;
    }
    Amplitude.trackGenerateReportRequest(type);
    triggerReportGeneration(type, dayjs().toISOString());
  };

  const handleRetryReport = (report: Reports.ReportQueryData) => {
    if (BULK_XLSX_TYPES.has(report.type) && report.report_id && organization?.workspace_id) {
      retryBulkXlsx({ report, workspaceId: organization.workspace_id });
      return;
    }
    triggerReportGeneration(report.type, dayjs().toISOString());
  };

  const handleOpenNDVIReportDrawer = () => {
    onClose();
    setNDVIReportDrawerVisible?.(true);
  };

  return (
    <Drawer
      visible={visible}
      onClose={onClose}
      placement="right"
      width={480}
      title={
        <div className="flex items-center">
          <h1 className="text-xl font-semibold">{t('header.reports.title')}</h1>
        </div>
      }
      closeIcon={<CloseIcon />}
      headerStyle={{
        padding: '24px 16px',
      }}
      bodyStyle={{
        padding: 0,
      }}
    >
      <div className="flex flex-col px-4 pt-4 overflow-x-hidden">
        <div className="pb-4">
          <p className="text-xs text-sy-neutral-60 font-semibold uppercase mb-0">
            {t('header.reports.drawer.automatic_delivery')}
          </p>
          <button
            type="button"
            onClick={handleOpenSubscriptions}
            className="flex items-center gap-3 h-[60px] px-3 py-[14px] rounded-lg hover:bg-sy-neutral-10 cursor-pointer transition-colors mt-2 border-0 bg-transparent text-left"
          >
            <div className="w-8 h-8 flex items-center justify-center bg-sy-neutral-10 rounded flex-shrink-0">
              <SubscriptionsIcon fill="#696F88" size={18} />
            </div>
            <div className="flex flex-col gap-0">
              <span className="text-sm">{t('header.reports.drawer.manage_subscriptions')}</span>
              <span className="text-sm text-sy-neutral-60">
                {t('header.reports.drawer.manage_subscriptions_subtitle')}
              </span>
            </div>
          </button>
        </div>

        <div className="pt-1 mb-2">
          <p className="text-xs text-sy-neutral-60 font-semibold uppercase mb-0">
            {t('header.reports.drawer.export_reports')}
          </p>
          <div className="flex flex-col mt-2 gap-2">
            <div className="grid grid-cols-2">
              <button
                type="button"
                onClick={() => onClickGenerateReport(Reports.ReportTypes.NDVI_CHANGE)}
                className="flex items-center gap-3 h-[60px] rounded-lg px-3 py-[14px] hover:bg-sy-neutral-10 cursor-pointer transition-colors border-0 bg-transparent text-left"
              >
                <div className="w-8 h-8 flex items-center  justify-center bg-[#DFFBE8] rounded flex-shrink-0">
                  {loadingReports[Reports.ReportTypes.NDVI_CHANGE] ? (
                    <Spin indicator={<LoadingOutlined spin />} />
                  ) : (
                    <SatelliteIcon fill="#14803C" />
                  )}
                </div>
                <span className="text-sm">{t('header.reports.drawer.ndvi_change_report')}</span>
              </button>

              <button
                type="button"
                onClick={() => onClickGenerateReport(Reports.ReportTypes.VARIABILITY_CHANGE)}
                className="flex items-center gap-3 h-[60px] rounded-lg px-3 py-[14px] hover:bg-sy-neutral-10 cursor-pointer transition-colors border-0 bg-transparent text-left"
              >
                <div className="w-8 h-8 flex items-center justify-center bg-[#FFF3DD] rounded flex-shrink-0">
                  {loadingReports[Reports.ReportTypes.VARIABILITY_CHANGE] ? (
                    <Spin indicator={<LoadingOutlined spin />} />
                  ) : (
                    <ContrastIcon fill="#9A6412" />
                  )}
                </div>
                <span className="text-sm">
                  {t('header.reports.drawer.variability_change_report')}
                </span>
              </button>

              <button
                type="button"
                onClick={() => {
                  onClose();
                  onOpenBulkXlsxReport?.('timeseries');
                }}
                className="flex items-center gap-3 h-[60px] rounded-lg px-3 py-[14px] hover:bg-sy-neutral-10 cursor-pointer transition-colors border-0 bg-transparent text-left"
                data-testid="time-series-button"
              >
                <div className="w-8 h-8 flex items-center justify-center bg-[#E8F4FF] rounded flex-shrink-0">
                  <TimeSeriesIcon fill="#0F6CD1" />
                </div>
                <span className="text-sm">
                  {t('header.reports.drawer.time_series_xlsx_report')}
                </span>
              </button>

              <button
                type="button"
                onClick={() => {
                  onClose();
                  onOpenBulkXlsxReport?.('histogram');
                }}
                className="flex items-center gap-3 h-[60px] rounded-lg px-3 py-[14px] hover:bg-sy-neutral-10 cursor-pointer transition-colors border-0 bg-transparent text-left"
                data-testid="histogram-button"
              >
                <div className="w-8 h-8 flex items-center justify-center bg-sy-pink-10 rounded flex-shrink-0">
                  <BarChartIcon fill="#BB485D" />
                </div>
                <span className="text-sm">{t('header.reports.drawer.histogram_xlsx_report')}</span>
              </button>
            </div>

            {hasAccessToWeeklyFieldInsightsReport && (
              <button
                type="button"
                onClick={handleOpenNDVIReportDrawer}
                className="flex items-center gap-3 h-[60px] px-3 py-[14px] rounded-lg w-[50%] hover:bg-sy-neutral-10 cursor-pointer transition-colors border-0 bg-transparent text-left"
              >
                <div className="w-8 h-8 flex items-center justify-center bg-[#E0F9F7] rounded flex-shrink-0">
                  <CalendarDateRangeIcon />
                </div>
                <span className="text-sm">
                  {t('header.reports.drawer.weekly_field_insights_report')}
                </span>
              </button>
            )}
          </div>
        </div>

        <ExportedReports reports={validQueries} onRetryReport={handleRetryReport} />
      </div>
    </Drawer>
  );
};

export default ReportsDrawer;
